import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, CheckCircle2, Loader2, Plus, Unlock, X } from 'lucide-react';
import { propsFundoModal } from '../../../src/lib/backdropModal';
import { RateioAjusteStatus, RateioAjusteUc, RateioCredito } from '../../../types';
import { carregarHistoricoRateiosDaUc } from './saldosUcsDados';
import {
  conferirAjuste,
  DIVERGENCIAS,
  formatarKwh,
  formatarPercentRateio,
  MARGEM_ERRO_MODELO,
  STATUS_AJUSTE,
  TIPOS_AJUSTE,
  ajusteAberto,
  type LinhaSaldoUc,
  type UgPainel,
} from './saldosUcs';
import { FAIXA_META, TENDENCIA_META } from './saldosUcsVisual';

interface SaldoUcDetalheModalProps {
  linha: LinhaSaldoUc;
  mesRateio: string;
  rateios: RateioCredito[];
  ugs: UgPainel[];
  ajustes: RateioAjusteUc[];
  atualizandoAjusteId: string | null;
  onMudarStatusAjuste: (ajuste: RateioAjusteUc, status: RateioAjusteStatus) => void;
  onRegistrarAjuste: () => void;
  /**
   * Só chega quando a UC está "Pausada" no cadastro e quem vê é gestor. O card de
   * travadas do Simulador só lista UC com usina vinculada (6 das 27 pausadas fora
   * do rateio em 14/10/2026): sem isto, as outras não tinham onde ser destravadas.
   */
  onDestravar?: () => Promise<void>;
  onClose: () => void;
}

const FONTE_CONSUMO: Record<string, string> = {
  observado: 'média das faturas',
  estimativa_cadastral: 'estimativa do cadastro',
  indisponivel: 'sem consumo',
};

const Bloco: React.FC<{ titulo: string; children: React.ReactNode; extra?: React.ReactNode }> = ({ titulo, children, extra }) => (
  <section className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 p-4">
    <div className="flex items-center justify-between gap-2 mb-3">
      <h3 className="text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">{titulo}</h3>
      {extra}
    </div>
    {children}
  </section>
);

const nomeUsina = (numero: string | null | undefined, ugs: UgPainel[]) => {
  if (!numero) return '—';
  const ug = ugs.find(u => (u.numero_uc_geradora_antigo || '').replace(/\D/g, '') === numero) ||
    ugs.find(u => (u.numero_uc_geradora || '').replace(/\D/g, '') === numero);
  return ug ? `${ug.nome} (${numero})` : numero;
};

const SaldoUcDetalheModal: React.FC<SaldoUcDetalheModalProps> = ({
  linha,
  mesRateio,
  rateios,
  ugs,
  ajustes,
  atualizandoAjusteId,
  onMudarStatusAjuste,
  onRegistrarAjuste,
  onDestravar,
  onClose,
}) => {
  const [historicoRateios, setHistoricoRateios] = useState<Awaited<ReturnType<typeof carregarHistoricoRateiosDaUc>> | null>(null);
  const [destravando, setDestravando] = useState(false);

  const destravar = async () => {
    if (!onDestravar || destravando) return;
    setDestravando(true);
    try {
      await onDestravar();
    } finally {
      setDestravando(false);
    }
  };

  useEffect(() => {
    let ativo = true;
    carregarHistoricoRateiosDaUc([linha.ucNumero, linha.ucNovo || ''], rateios)
      .then(h => { if (ativo) setHistoricoRateios(h); })
      .catch(err => { console.error('Erro ao carregar histórico de rateios da UC', err); if (ativo) setHistoricoRateios([]); });
    return () => { ativo = false; };
  }, [linha.ucNumero, linha.ucNovo, rateios]);

  const s = linha.sugestao;
  const faixa = linha.faixa ? FAIXA_META[linha.faixa] : null;
  const usinasDistintas = new Set((historicoRateios || []).map(h => h.ugNumero).filter(Boolean)).size;

  return createPortal(
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in" {...propsFundoModal(onClose)}>
      <div className="bg-gray-50 dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[92vh] flex flex-col overflow-hidden animate-slide-up" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 p-5 bg-white dark:bg-gray-800 border-b border-gray-100 dark:border-gray-700">
          <div className="min-w-0">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white font-poppins truncate">{linha.nome || 'UC sem nome'}</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              UC {linha.ucNumero}
              {linha.ucNovo && linha.ucNovo !== linha.ucNumero ? ` · nova ${linha.ucNovo}` : ''}
              {linha.cpfCnpj ? ` · ${linha.cpfCnpj}` : ''}
              {linha.tipoLigacao ? ` · ${linha.tipoLigacao}` : ''}
            </p>
            <div className="flex flex-wrap gap-1.5 mt-2">
              <span className="px-2 py-0.5 rounded-md text-[11px] font-semibold bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200">
                Rateio {mesRateio}: {linha.statusPlanilha || '—'}
              </span>
              <span className="px-2 py-0.5 rounded-md text-[11px] font-semibold bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200">
                Cadastro: {linha.statusCadastro || 'sem situação'}
              </span>
              <span className="px-2 py-0.5 rounded-md text-[11px] font-semibold bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200">
                Usina: {linha.ugNome || linha.ugNumero || '—'}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {onDestravar ? (
              <button
                onClick={destravar}
                disabled={destravando}
                title='Tira a UC de "Pausada" (vai para Reativação). Sem isso, a planilha do rateio não muda a situação dela no cadastro.'
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold border border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 hover:bg-amber-100 disabled:opacity-50"
              >
                {destravando ? <Loader2 size={14} className="animate-spin" /> : <Unlock size={14} />} Destravar
              </button>
            ) : null}
            <button onClick={onRegistrarAjuste} className="flex items-center gap-1.5 px-3 py-2 bg-primary text-white rounded-lg text-xs font-bold hover:opacity-90">
              <Plus size={14} /> Registrar ajuste
            </button>
            <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full" aria-label="Fechar">
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto">
          {linha.divergencias.length > 0 ? (
            <div className="rounded-xl border border-amber-200 dark:border-amber-800/50 bg-amber-50 dark:bg-amber-900/20 p-4 space-y-1.5">
              {linha.divergencias.map(d => (
                <p key={d} className="flex gap-2 text-xs text-amber-800 dark:text-amber-300">
                  <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
                  <span><strong>{DIVERGENCIAS[d].rotulo}.</strong> {DIVERGENCIAS[d].descricao}</span>
                </p>
              ))}
            </div>
          ) : null}

          <div className="grid md:grid-cols-2 gap-4">
            <Bloco titulo="Saldo de créditos">
              <div className="flex items-end gap-3 mb-3">
                <p className="text-2xl font-bold text-gray-900 dark:text-white tabular-nums">
                  {formatarKwh(linha.saldo?.kwh)} <span className="text-sm font-medium text-gray-400">kWh</span>
                </p>
                {faixa && linha.cobertura ? (
                  <span className={`mb-1 px-2 py-0.5 rounded-md text-[11px] font-bold ${faixa.chip}`}>
                    {linha.cobertura.coberturaMeses.toLocaleString('pt-BR')} {linha.cobertura.coberturaMeses === 1 ? 'mês' : 'meses'} · {faixa.rotulo}
                  </span>
                ) : null}
              </div>
              <p className="text-[11px] text-gray-500 dark:text-gray-400 mb-3">
                {linha.saldo
                  ? `Saldo da fatura de ${linha.saldo.mes}${linha.ultimaFatura && linha.ultimaFatura.mes !== linha.saldo.mes ? ` (a de ${linha.ultimaFatura.mes} veio sem saldo lido)` : ''}. `
                  : linha.ultimaFatura ? 'Nenhuma fatura recente trouxe o saldo lido. ' : 'Sem fatura nos últimos 6 meses. '}
                {linha.tendencia ? `Nos últimos meses o saldo está ${TENDENCIA_META[linha.tendencia].rotulo.toLowerCase()}.` : ''}
              </p>
              {linha.historico.length > 0 ? (
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-gray-400 dark:text-gray-500">
                      <th className="py-1 font-semibold">Mês</th>
                      <th className="py-1 font-semibold text-right">Consumo</th>
                      <th className="py-1 font-semibold text-right">Crédito recebido</th>
                      <th className="py-1 font-semibold text-right">Saldo</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {linha.historico.map((f, i) => (
                      <tr key={`${f.mes_referencia}-${i}`} className="text-gray-700 dark:text-gray-200 tabular-nums">
                        <td className="py-1.5">{f.mes_referencia}</td>
                        <td className="py-1.5 text-right">{formatarKwh(f.consumo_ativo_kwh)}</td>
                        <td className="py-1.5 text-right">{formatarKwh(f.credito_recebido_kwh ?? f.energia_injetada_kwh)}</td>
                        <td className="py-1.5 text-right font-semibold">{formatarKwh(f.saldo_creditos_kwh)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : null}
            </Bloco>

            <Bloco titulo="% do rateio">
              <div className="grid grid-cols-3 gap-2 mb-3">
                {[
                  { rotulo: 'Vigente', valor: formatarPercentRateio(linha.percentVigente), destaque: true },
                  { rotulo: 'Rateio anterior', valor: formatarPercentRateio(linha.percentAnterior) },
                  { rotulo: 'Sugerido', valor: formatarPercentRateio(s?.percent) },
                ].map(c => (
                  <div key={c.rotulo} className={`rounded-lg p-2 ${c.destaque ? 'bg-primary/10' : 'bg-gray-50 dark:bg-gray-900/40'}`}>
                    <p className="text-[10px] font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">{c.rotulo}</p>
                    <p className="text-base font-bold text-gray-900 dark:text-white tabular-nums">{c.valor}</p>
                  </div>
                ))}
              </div>
              {s ? (
                <div className="text-xs text-gray-600 dark:text-gray-300 space-y-1 tabular-nums">
                  <p className="font-semibold text-gray-500 dark:text-gray-400">Como o sugerido é calculado (modelo da planilha):</p>
                  <p>
                    Consumo {formatarKwh(s.consumoKwh)} kWh
                    {linha.consumo.fonte ? <span className="text-gray-400"> ({FONTE_CONSUMO[linha.consumo.fonte] || linha.consumo.fonte})</span> : null}
                    {' '}− mínimo {s.custoDisponibilidadeKwh} kWh = <strong>{formatarKwh(s.compensavelKwh, 2)} kWh compensável</strong>
                  </p>
                  {s.ajustadoKwh != null ? (
                    <p>
                      × {(1 + MARGEM_ERRO_MODELO).toLocaleString('pt-BR')} (margem de {Math.round(MARGEM_ERRO_MODELO * 100)}%) = <strong>{formatarKwh(s.ajustadoKwh, 2)} kWh</strong>
                    </p>
                  ) : null}
                  {s.geracaoMediaKwh ? (
                    <p>
                      ÷ {formatarKwh(s.geracaoMediaKwh)} kWh de geração média da usina = <strong>{formatarPercentRateio(s.percent)}</strong>
                    </p>
                  ) : null}
                  {s.motivo ? <p className="text-amber-700 dark:text-amber-400">{s.motivo}</p> : null}
                  {linha.consumo.suspeito ? (
                    <p className="text-amber-700 dark:text-amber-400">Consumo instável entre os meses: confira o histórico antes de usar a sugestão.</p>
                  ) : null}
                </div>
              ) : (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {linha.recebe ? 'Sem sugestão: a UC está em mais de uma geradora, e a divisão entre elas é manual.' : 'A UC não recebe energia neste rateio, então não há % sugerido.'}
                </p>
              )}
            </Bloco>
          </div>

          <Bloco
            titulo="Histórico nos rateios"
            extra={usinasDistintas > 1 ? (
              <span className="text-[11px] font-semibold text-amber-700 dark:text-amber-400">Passou por {usinasDistintas} usinas</span>
            ) : null}
          >
            {historicoRateios == null ? (
              <p className="flex items-center gap-2 text-xs text-gray-400"><Loader2 size={14} className="animate-spin" /> Carregando…</p>
            ) : historicoRateios.length === 0 ? (
              <p className="text-xs text-gray-400">A UC não aparece em outros rateios.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-gray-400 dark:text-gray-500">
                      <th className="py-1 font-semibold">Rateio</th>
                      <th className="py-1 font-semibold">Status</th>
                      <th className="py-1 font-semibold">Usina</th>
                      <th className="py-1 font-semibold text-right">%</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {historicoRateios.map((h, i) => (
                      <tr key={`${h.mes}-${i}`} className="text-gray-700 dark:text-gray-200">
                        <td className="py-1.5 font-semibold">{h.mes}</td>
                        <td className="py-1.5">{h.status || '—'}</td>
                        <td className="py-1.5">{nomeUsina(h.ugNumero, ugs)}</td>
                        <td className="py-1.5 text-right tabular-nums">{formatarPercentRateio(h.percent)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Bloco>

          <Bloco titulo={`Ajustes (${ajustes.length})`}>
            {ajustes.length === 0 ? (
              <p className="text-xs text-gray-400">Nenhum ajuste registrado para esta UC.</p>
            ) : (
              <ul className="space-y-2">
                {ajustes.map(a => {
                  const conferencia = ajusteAberto(a) ? conferirAjuste(a, linha, mesRateio) : null;
                  const atualizando = atualizandoAjusteId === a.id;
                  return (
                    <li key={a.id} className="rounded-lg border border-gray-100 dark:border-gray-700 p-3">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <p className="text-sm font-semibold text-gray-800 dark:text-white">
                          {TIPOS_AJUSTE[a.tipo]}
                          {a.tipo === 'alterar_percentual' ? (
                            <span className="font-normal text-gray-500 dark:text-gray-400 tabular-nums">
                              {' '}· {formatarPercentRateio(a.percent_atual)} → <strong className="text-gray-800 dark:text-white">{formatarPercentRateio(a.percent_novo)}</strong>
                            </span>
                          ) : null}
                          {a.tipo === 'trocar_geradora' ? (
                            <span className="font-normal text-gray-500 dark:text-gray-400"> · {nomeUsina(a.ug_atual, ugs)} → {nomeUsina(a.ug_nova, ugs)}</span>
                          ) : null}
                          {a.tipo === 'remanejar_saldo' ? (
                            <span className="font-normal text-gray-500 dark:text-gray-400"> · de {nomeUsina(a.ug_remanejamento, ugs)}</span>
                          ) : null}
                        </p>
                        <span className={`px-2 py-0.5 rounded-md text-[11px] font-bold ${
                          a.status === 'aplicado' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                            : a.status === 'cancelado' ? 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                              : a.status === 'enviado' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                                : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                        }`}>
                          {STATUS_AJUSTE[a.status]}
                        </span>
                      </div>
                      <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
                        Para o rateio de {a.mes_rateio_alvo || '—'} · registrado por {a.criado_por_nome || '—'} em {new Date(a.created_at).toLocaleDateString('pt-BR')}
                        {a.status_atualizado_em ? ` · ${STATUS_AJUSTE[a.status].toLowerCase()} por ${a.status_atualizado_por_nome || '—'} em ${new Date(a.status_atualizado_em).toLocaleDateString('pt-BR')}` : ''}
                      </p>
                      {a.motivo ? <p className="text-xs text-gray-700 dark:text-gray-300 mt-1.5 whitespace-pre-wrap">{a.motivo}</p> : null}
                      {conferencia === 'confirmado' ? (
                        <p className="flex items-center gap-1.5 text-[11px] font-semibold text-emerald-700 dark:text-emerald-400 mt-2">
                          <CheckCircle2 size={13} /> O rateio de {mesRateio} já veio como pedido.
                        </p>
                      ) : conferencia === 'nao_confirmado' ? (
                        <p className="flex items-center gap-1.5 text-[11px] font-semibold text-red-600 dark:text-red-400 mt-2">
                          <AlertTriangle size={13} /> O rateio de {mesRateio} não veio como pedido.
                        </p>
                      ) : null}
                      {ajusteAberto(a) ? (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {a.status === 'pendente' ? (
                            <button disabled={atualizando} onClick={() => onMudarStatusAjuste(a, 'enviado')}
                              className="px-2.5 py-1 rounded-md text-[11px] font-bold border border-blue-200 dark:border-blue-800/50 text-blue-700 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 disabled:opacity-50">
                              Marcar como enviado
                            </button>
                          ) : null}
                          <button disabled={atualizando} onClick={() => onMudarStatusAjuste(a, 'aplicado')}
                            className="px-2.5 py-1 rounded-md text-[11px] font-bold border border-emerald-200 dark:border-emerald-800/50 text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 hover:bg-emerald-100 disabled:opacity-50">
                            Marcar como aplicado
                          </button>
                          <button disabled={atualizando} onClick={() => onMudarStatusAjuste(a, 'cancelado')}
                            className="px-2.5 py-1 rounded-md text-[11px] font-bold border border-gray-200 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50">
                            Cancelar
                          </button>
                          {atualizando ? <Loader2 size={14} className="animate-spin text-gray-400 self-center" /> : null}
                        </div>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            )}
          </Bloco>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default SaldoUcDetalheModal;
